/**
 * Host-side input pollers: turn the live @engine/input devices into the per-frame
 * {@link UIPointerSnapshot}/{@link UINavSnapshot} that {@link UIInputSystem} consumes.
 * SHARED by the editor UISubsystem and the player UISubsystem so the two hosts cannot
 * drift on device mapping (the system already owns all event semantics).
 */
import type { InputManager } from '@engine/input';
import {
  EMPTY_NAV,
  EMPTY_POINTER,
  type UINavSnapshot,
  type UIPointerSnapshot,
} from './UIInputSystem';

/**
 * Mouse (window client coords) → canvas BACKING px; `inside` bounds the viewport.
 * When the canvas has no usable client rect (native player's canvas shim), pointer
 * coordinates are assumed to already be in canvas space (the .NET host dispatches
 * synthetic events directly against the canvas).
 */
export function pollPointerFromCanvas(
  manager: InputManager,
  canvas: HTMLCanvasElement,
): UIPointerSnapshot {
  const mouse = manager.mouse;
  if (!mouse) return EMPTY_POINTER;

  const pos = mouse.position.value;
  let x = pos.x;
  let y = pos.y;
  const rect =
    typeof canvas.getBoundingClientRect === 'function' ? canvas.getBoundingClientRect() : null;
  if (rect && rect.width > 0 && rect.height > 0) {
    x = (pos.x - rect.left) * (canvas.width / rect.width);
    y = (pos.y - rect.top) * (canvas.height / rect.height);
  }

  return {
    x,
    y,
    inside: x >= 0 && y >= 0 && x < canvas.width && y < canvas.height,
    primary: mouse.leftButton.isPressed,
    secondary: mouse.rightButton.isPressed,
    middle: mouse.middleButton.isPressed,
    wheelX: mouse.scroll.value.x,
    wheelY: mouse.scroll.value.y,
  };
}

/**
 * Navigation snapshot: built-in defaults (arrows/Enter/Escape + dpad/left-stick/A/B — Unity
 * standalone-module style, active regardless of the game's maps) — unless the game's input
 * asset defines a `UI` ActionMap (actions `Navigate` axis2d / `Submit` / `Cancel`), which
 * then REPLACES the defaults (design §Input: "map through a `ui` ActionMap").
 */
export function pollNavFromInput(manager: InputManager): UINavSnapshot {
  const map = manager.getActionMap('UI') ?? manager.getActionMap('ui');
  if (map?.enabled) {
    const navigate = map.getAction('Navigate');
    const submit = map.getAction('Submit');
    const cancel = map.getAction('Cancel');
    if (navigate || submit || cancel) {
      const v = navigate?.vector2 ?? { x: 0, y: 0 };
      return {
        // composite convention: up = +y (InputAction composite parts)
        up: v.y > 0.5,
        down: v.y < -0.5,
        left: v.x < -0.5,
        right: v.x > 0.5,
        submit: submit?.isPressed ?? false,
        cancel: cancel?.isPressed ?? false,
      };
    }
  }

  const nav: UINavSnapshot = { ...EMPTY_NAV };
  const kb = manager.keyboard;
  if (kb) {
    nav.up = kb.isKeyPressed('ArrowUp');
    nav.down = kb.isKeyPressed('ArrowDown');
    nav.left = kb.isKeyPressed('ArrowLeft');
    nav.right = kb.isKeyPressed('ArrowRight');
    nav.submit = kb.isKeyPressed('Enter') || kb.isKeyPressed('NumpadEnter');
    nav.cancel = kb.isKeyPressed('Escape');
  }
  for (const gp of manager.gamepads) {
    const stick = gp.leftStick.value; // Gamepad API convention: up = -y
    nav.up ||= gp.dpadUp.isPressed || stick.y < -0.5;
    nav.down ||= gp.dpadDown.isPressed || stick.y > 0.5;
    nav.left ||= gp.dpadLeft.isPressed || stick.x < -0.5;
    nav.right ||= gp.dpadRight.isPressed || stick.x > 0.5;
    nav.submit ||= gp.a.isPressed;
    nav.cancel ||= gp.b.isPressed;
  }
  return nav;
}
